import type { ReactNode } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import {
  Autocomplete,
  Box,
  Checkbox,
  Drawer,
  IconButton,
  InputAdornment,
  InputBase,
  ListItemText,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFormContext, useController } from 'react-hook-form';
import { useI18n } from 'iomart-hooks';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import TextMaxLine from 'src/components/text-max-line';
import { getFormattedValue } from 'src/utils/common';
import type { Columns } from 'src/@types/common';

// ----------------------------------------------------------------------

const DictionaryCellContent = ({ row, col }: { row: any; col: Columns }) => {
  const { name, subname, format, subFormat, params, nameLines, subNameLines } = col;
  const content = getFormattedValue(row, name, format, params);
  const mainStr = String(
    (typeof content === 'string' ? content : content ?? '-') || '-'
  );
  const subContent = subname ? getFormattedValue(row, subname, subFormat, params) : null;
  const subStr = subContent != null ? String(subContent) || '-' : '';
  const tooltipTitle = subStr ? `${mainStr}\n${subStr}` : mainStr;
  const lineSx = { minWidth: 0, width: '100%' };

  const cellContent = subname ? (
    <Box sx={{ minWidth: 0, maxWidth: 1, overflow: 'hidden', width: 1 }}>
      <TextMaxLine line={nameLines ?? 1} variant="caption" sx={lineSx}>
        {mainStr}
      </TextMaxLine>
      <TextMaxLine
        line={subNameLines ?? 1}
        variant="caption"
        sx={{ ...lineSx, color: 'text.disabled', mt: 0.25 }}
      >
        {subStr}
      </TextMaxLine>
    </Box>
  ) : (
    <TextMaxLine line={nameLines ?? 1} variant="caption" sx={lineSx}>
      {mainStr}
    </TextMaxLine>
  );

  return (
    <Tooltip
      title={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{tooltipTitle}</Box>}
      placement="top-start"
      enterDelay={400}
    >
      <Box component="span" sx={{ display: 'block', minWidth: 0, overflow: 'hidden' }}>
        {cellContent}
      </Box>
    </Tooltip>
  );
};

// ----------------------------------------------------------------------

const AutocompleteListbox = forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
  (props, ref) => {
    const { className, style, ...rest } = props;
    return (
      <Scrollbar
        className={className}
        mobileBehavior="custom"
        sx={{ maxHeight: '40vh', overflow: 'hidden' }}
      >
        <ul ref={ref} {...rest} style={{ ...style, overflow: 'visible', maxHeight: 'none' }} />
      </Scrollbar>
    );
  }
);
AutocompleteListbox.displayName = 'AutocompleteListbox';

// ----------------------------------------------------------------------

type Props = {
  name: string;
  label: string;
  /** Локальные опции (без API) */
  options: any[];
  /** Ключ идентификации опции. По умолчанию 'id'. Для объектов с index — 'index' */
  optionsIdKey?: string;
  /** Функция извлечения значения для формы при выборе. По умолчанию (opt) => opt */
  getValueFromOption?: (opt: any) => any;
  optionsLabel?: string;
  getOptionLabel?: (option: any) => string;
  columns: Columns[];
  multiple?: boolean;
  searchName?: string;
  pageSize?: number;
  disabled?: boolean;
  drawerWidth?: Record<string, string | number>;
  headerActions?: ReactNode;
  onChange?: (value: any) => void;
};

const searchStr = (row: any, searchName: string) =>
  String(row?.hsCode ?? '') + ' ' + String(row?.description ?? '') + ' ' + String(row?.[searchName] ?? '');

export default function RHFLocalDictionary({
  name,
  label,
  options = [],
  optionsIdKey = 'id',
  getValueFromOption = (opt: any) => opt,
  optionsLabel = 'name',
  getOptionLabel: getOptionLabelProp,
  columns,
  multiple = false,
  searchName = 'search',
  pageSize = 10,
  disabled,
  drawerWidth = { xs: '100%', sm: 560 },
  headerActions,
  onChange,
}: Props) {
  const { t } = useI18n(false);
  const { control, setValue } = useFormContext();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: multiple ? [] : null,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const debouncedSetSearch = useMemo(
    () =>
      debounce((v: string) => {
        setDebouncedSearch(v);
        setPage(0);
      }, 400),
    []
  );
  useEffect(() => {
    debouncedSetSearch(searchInput);
    return () => debouncedSetSearch.cancel();
  }, [searchInput, debouncedSetSearch]);

  useEffect(() => {
    if (drawerOpen) setPage(0);
  }, [drawerOpen]);

  const filteredItems = useMemo(() => {
    const search = debouncedSearch.trim().toLowerCase();
    if (!search) return options;
    return options.filter((row: any) =>
      searchStr(row, searchName).toLowerCase().includes(search)
    );
  }, [options, debouncedSearch, searchName]);

  const autocompleteFiltered = useMemo(() => {
    const search = inputValue.trim().toLowerCase();
    if (!search) return options;
    return options.filter((row: any) =>
      searchStr(row, searchName).toLowerCase().includes(search)
    );
  }, [options, inputValue, searchName]);

  const getOptionLabel = (opt: any) => {
    if (opt == null) return '';
    const raw = (getOptionLabelProp ?? ((o: any) => (o && o[optionsLabel]) ?? ''))(opt);
    return raw === '#undefined' || raw === '#null' ? '' : raw;
  };

  const { value } = field;
  const getRowId = useCallback(
    (row: any) =>
      optionsIdKey && row && row[optionsIdKey] !== undefined
        ? row[optionsIdKey]
        : getValueFromOption(row),
    [optionsIdKey, getValueFromOption]
  );
  const getStoredId = (v: any) =>
    v != null && typeof v === 'object' ? getRowId(v) : v;
  const selectedIds: any[] = multiple
    ? (Array.isArray(value) ? value : []).map(getStoredId)
    : value != null ? [getStoredId(value)] : [];

  const displayColumns = columns.filter(
    (col) => !['status'].includes(col.name) && col.groupType !== 'statuses'
  );

  const getCellSx = (col: Columns & { minWidth?: number; maxWidth?: number }, extra: Record<string, any> = {}) => {
    const { width, minWidth, maxWidth } = col;
    return {
      ...(width !== undefined ? { width } : {}),
      ...(minWidth !== undefined ? { minWidth } : { minWidth: 0 }),
      ...(maxWidth !== undefined ? { maxWidth } : {}),
      overflow: 'hidden',
      ...extra,
    };
  };

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const paginatedItems = filteredItems.slice(page * pageSize, (page + 1) * pageSize);

  const handleSelect = useCallback(
    (row: any) => {
      const rowId = getRowId(row);
      const isSelected = selectedIds.includes(rowId);
      if (!multiple && isSelected) {
        setValue(name, null, { shouldValidate: true, shouldDirty: true });
        onChange?.(null);
        setDrawerOpen(false);
        return;
      }
      const valToStore = getValueFromOption(row);
      if (multiple) {
        const current = Array.isArray(value) ? value : [];
        const idx = current.findIndex((v: any) => getStoredId(v) === rowId);
        const next = idx >= 0 ? current.filter((_: any, i: number) => i !== idx) : [...current, valToStore];
        setValue(name, next, { shouldValidate: true, shouldDirty: true });
        onChange?.(next);
      } else {
        setValue(name, valToStore, { shouldValidate: true, shouldDirty: true });
        onChange?.(valToStore);
        setDrawerOpen(false);
      }
    },
    [multiple, selectedIds, value, name, setValue, onChange, getValueFromOption, getRowId]
  );

  const handleCheckboxChange = useCallback(
    (row: any, checked: boolean) => {
      if (!multiple) return;
      const current = Array.isArray(value) ? value : [];
      const rowId = getRowId(row);
      const match = (v: any) => getStoredId(v) === rowId;
      if (checked) {
        setValue(
          name,
          current.some(match) ? current : [...current, getValueFromOption(row)],
          { shouldValidate: true, shouldDirty: true }
        );
      } else {
        setValue(name, current.filter((v) => !match(v)), { shouldValidate: true, shouldDirty: true });
      }
    },
    [multiple, value, name, setValue, getValueFromOption, getRowId]
  );

  const handleSelectAllInPage = useCallback(() => {
    if (!multiple || paginatedItems.length === 0) return;
    const current = Array.isArray(value) ? value : [];
    const currentIds = new Set(current.map(getStoredId));
    const allOnPageSelected = paginatedItems.every((row: any) => currentIds.has(getRowId(row)));
    if (allOnPageSelected) {
      const idsOnPage = new Set(paginatedItems.map((r: any) => getRowId(r)));
      setValue(
        name,
        current.filter((v: any) => !idsOnPage.has(getStoredId(v))),
        { shouldValidate: true, shouldDirty: true }
      );
    } else {
      const toAdd = paginatedItems
        .filter((row: any) => !currentIds.has(getRowId(row)))
        .map((r: any) => getValueFromOption(r));
      setValue(name, [...current, ...toAdd], { shouldValidate: true, shouldDirty: true });
    }
  }, [multiple, paginatedItems, value, name, setValue, getValueFromOption, getRowId]);

  const resolveToOption = useCallback(
    (val: any) => {
      if (val == null) return null;
      if (typeof val === 'object') return options.find((o: any) => getRowId(o) === getRowId(val)) ?? val;
      return options.find((o: any) => getValueFromOption(o) === val) ?? null;
    },
    [options, getRowId, getValueFromOption]
  );
  const selectedItems = useMemo(
    () =>
      multiple
        ? (Array.isArray(value) ? value : []).map(resolveToOption).filter(Boolean)
        : resolveToOption(value) ? [resolveToOption(value)] : [],
    [multiple, value, resolveToOption]
  );

  const tableMinWidth =
    displayColumns.reduce((sum, col) => {
      const w = (col as any).width ?? (col as any).minWidth ?? 80;
      return sum + (Number.isFinite(w) ? w : 80);
    }, multiple ? 48 : 48);

  const autocompleteValue = multiple ? selectedItems : (selectedItems[0] ?? null);

  const autocompleteOptionsWithValue = useMemo(() => {
    const opts = autocompleteFiltered;
    const vals = multiple ? selectedItems : (selectedItems[0] ? [selectedItems[0]] : []);
    if (vals.length === 0) return opts;
    const existingIds = new Set(opts.map((o: any) => getRowId(o)));
    const toAdd = vals.filter((v: any) => v != null && !existingIds.has(getRowId(v)));
    return toAdd.length === 0 ? opts : [...toAdd, ...opts];
  }, [autocompleteFiltered, selectedItems, multiple, getRowId]);

  const handleAutocompleteChange = useCallback(
    (_: any, newValue: any) => {
      if (multiple) {
        const next = (Array.isArray(newValue) ? newValue : [])
          .map((v: any) => (v && typeof v === 'object' ? getValueFromOption(v) : v));
        setValue(name, next, { shouldValidate: true, shouldDirty: true });
        onChange?.(next);
      } else {
        const currentId = value != null ? getStoredId(value) : null;
        const newId = newValue != null ? getValueFromOption(newValue) : null;
        const isSame = currentId != null && newId != null && String(currentId) === String(newId);
        const next = isSame ? null : (newValue != null ? getValueFromOption(newValue) : null);
        setValue(name, next, { shouldValidate: true, shouldDirty: true });
        onChange?.(next);
      }
    },
    [multiple, value, name, setValue, onChange, getValueFromOption, getStoredId]
  );

  return (
    <>
      <Autocomplete
        multiple={multiple}
        size="small"
        value={autocompleteValue}
        options={autocompleteOptionsWithValue}
        loading={false}
        open={autocompleteOpen}
        onOpen={() => {
          if (disabled) return;
          if (!multiple && selectedItems[0]) {
            setInputValue(getOptionLabel(selectedItems[0]));
          }
          setAutocompleteOpen(true);
        }}
        onClose={() => setAutocompleteOpen(false)}
        inputValue={inputValue}
        onInputChange={(_, v) => setInputValue(v)}
        onChange={handleAutocompleteChange}
        filterOptions={(opts) => opts}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(opt, val) =>
          (opt == null && val == null) || (opt != null && val != null && getRowId(opt) === getRowId(val))
        }
        ListboxComponent={AutocompleteListbox}
        disabled={disabled}
        disableClearable
        blurOnSelect
        renderOption={(props, option) => {
          const optId = getRowId(option);
          const isSelected = selectedIds.includes(optId);
          const { onClick: propsOnClick, ...restProps } = props;
          return (
            <li
              {...restProps}
              key={optId}
              style={{ ...props.style, display: 'flex', alignItems: 'center' }}
              onClick={(e) => {
                if (!multiple && isSelected) {
                  e.stopPropagation();
                  e.preventDefault();
                  setValue(name, null, { shouldValidate: true, shouldDirty: true });
                  onChange?.(null);
                  setAutocompleteOpen(false);
                  return;
                }
                propsOnClick?.(e);
              }}
            >
              <ListItemText
                primary={getOptionLabel(option)}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: isSelected ? { fontWeight: 600, color: 'primary.main' } : undefined,
                }}
                sx={{ flex: 1, minWidth: 0 }}
              />
              {isSelected && (
                <Box sx={{ ml: 1, flexShrink: 0 }}>
                  <Iconify icon="eva:checkmark-circle-2-fill" width={16} color="primary.main" />
                </Box>
              )}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t(label)}
            error={!!error}
            helperText={error?.message}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputBase-input': { font: 'menu' } }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end" sx={{ marginRight: 0 }}>
                  <Tooltip title={t('Dictionary')}>
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!disabled) setDrawerOpen(true);
                      }}
                      disabled={disabled}
                      edge="end"
                      size="small"
                      sx={{ marginRight: -5, marginLeft: 0 }}
                    >
                      <Iconify icon="mdi:book-open-variant" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSearchInput('');
        }}
        PaperProps={{ sx: { width: drawerWidth, maxWidth: '100%' } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6">{t(label)}</Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {headerActions}
                <Tooltip title={t('Close')}>
                  <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                    <Iconify icon="eva:close-fill" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
            <InputBase
              fullWidth
              placeholder={t('Search') + '...'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
              sx={{ borderRadius: 1, bgcolor: 'background.neutral', px: 1.5, py: 1 }}
            />
          </Box>

          <Scrollbar mobileBehavior="custom" sx={{ flex: 1, minHeight: 0 }}>
            {filteredItems.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 6,
                  px: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('No data')}
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ overflow: 'visible' }}>
                <Table size="small" stickyHeader sx={{ minWidth: tableMinWidth, tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 48, minWidth: 48 }} />
                    {displayColumns.map((col, colIdx) => (
                      <col
                        key={`${col.name}-${colIdx}`}
                        style={{
                          width: (col as any).width ?? (col as any).minWidth ?? 100,
                          minWidth: (col as any).minWidth ?? 80,
                        }}
                      />
                    ))}
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        padding="checkbox"
                        sx={{
                          width: 48,
                          minWidth: 48,
                          maxWidth: 48,
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                          backgroundColor: 'background.paper',
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          sx={{ ml: multiple ? -0.9 : -0.2 }}
                        >
                          {multiple && (
                            <Checkbox
                              checked={
                                paginatedItems.length > 0 &&
                                paginatedItems.every((row: any) => selectedIds.includes(getRowId(row)))
                              }
                              indeterminate={
                                paginatedItems.length > 0 &&
                                paginatedItems.some((row: any) => selectedIds.includes(getRowId(row))) &&
                                !paginatedItems.every((row: any) => selectedIds.includes(getRowId(row)))
                              }
                              onChange={handleSelectAllInPage}
                              title={t('Select all')}
                            />
                          )}
                          <Tooltip title={t('Clear')}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setValue(name, multiple ? [] : null, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  });
                                  if (!multiple) setDrawerOpen(false);
                                }}
                                disabled={multiple ? selectedIds.length === 0 : !value}
                                sx={{ p: 0.25 }}
                              >
                                <Iconify icon="eva:close-circle-outline" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      {displayColumns.map((col, colIdx) => (
                        <TableCell key={`${col.name}-${colIdx}`} align={col.align} sx={getCellSx(col)}>
                          {t(col.label)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedItems.map((row) => {
                      const rowId = getRowId(row);
                      const isSelected = selectedIds.includes(rowId);
                      return (
                        <TableRow
                          key={rowId}
                          hover
                          selected={isSelected}
                          onClick={() => !multiple && handleSelect(row)}
                          sx={{ cursor: multiple ? undefined : 'pointer' }}
                        >
                          {multiple ? (
                            <TableCell
                              padding="checkbox"
                              onClick={(e) => e.stopPropagation()}
                              sx={{ position: 'relative', zIndex: 1 }}
                            >
                              <Checkbox
                                checked={isSelected}
                                onChange={(_, checked) => handleCheckboxChange(row, checked)}
                              />
                            </TableCell>
                          ) : (
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Tooltip title={t('Select')}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleSelect(row)}
                                  color={isSelected ? 'primary' : 'default'}
                                >
                                  <Iconify
                                    icon={isSelected ? 'eva:checkmark-circle-2-fill' : 'eva:plus-circle-outline'}
                                  />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                          {displayColumns.map((col, colIdx) => (
                            <TableCell key={`${col.name}-${colIdx}`} align={col.align} sx={getCellSx(col)}>
                              <DictionaryCellContent row={row} col={col} />
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Scrollbar>

          {totalPages > 1 && (
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(_, p) => setPage(p - 1)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
}
